import {
  isEurekaDevice,
  isWaterLeakDetectionDevice,
} from "src/data/devices/logic/deviceLogic"
import { TDevice } from "src/data/devices/types/deviceTypes"
import { shouldDisplaySetting } from "src/data/deviceSettings/deviceCapabilityMapping"
import { EditType, ISection } from "src/data/editField/editFieldTypes"
import { TClockTypeMaybe, TTemperatureUnit } from "src/data/user/user"

import {
  IDeviceSettings,
  IDeviceSettingsPayload,
} from "./types/deviceSettingTypes"
import {
  ADVANCED_SETTINGS_SECTION,
  EurekaSectionAdvancedContents,
  NOTIFY_ABOUT_NOISE_ID,
  SectionAdvanced,
  WaterLeakSectionAdvancedContents,
} from "./sectionAdvanced"
import {
  GENERAL_SETTINGS_SECTION,
  OUTDOOR_SENSOR_ID,
  SectionGeneral,
} from "./sectionGeneral"
import {
  HUMIDITY_SECTION_KEY,
  HUMIDITY_SECTION_KEY_V2,
  SectionHumidityIndoorClimate,
  SectionHumidityV2,
} from "./sectionHumidity"
import { SectionNoiseMonitoring } from "./sectionNoiseMonitoring"
import {
  SectionTemperatureIndoorClimate,
  SectionTemperatureV2,
  TEMPERATURE_SECTION_KEY,
  TEMPERATURE_SECTION_KEY_V2,
} from "./sectionTemperature"

// Setting sections
const SECTIONS: ISection<IDeviceSettings, IDeviceSettingsPayload>[] = [
  SectionGeneral,
  SectionNoiseMonitoring,
  SectionHumidityIndoorClimate,
  SectionHumidityV2,
  SectionTemperatureIndoorClimate,
  SectionTemperatureV2,
  SectionAdvanced,
]

/** Filter out settings that should be hidden in bulk mode */
function filterBulkModeSettings({
  section,
  inBulkMode,
}: {
  section: ISection<IDeviceSettings, IDeviceSettingsPayload>
  inBulkMode: boolean
}): ISection<IDeviceSettings, IDeviceSettingsPayload> {
  if (!inBulkMode) {
    return section
  }

  return {
    ...section,
    contents: section.contents.filter(
      (setting) => !("hideInBulkMode" in setting && setting.hideInBulkMode)
    ),
  }
}

/** Return settings with default values */
export function getSections({
  devices,
  featureFlags,
}: {
  devices: TDevice[]
  featureFlags?: Record<string, boolean>
}): ISection<IDeviceSettings, IDeviceSettingsPayload>[] {
  const inBulkMode = devices.length > 1

  return (
    [...SECTIONS]
      .map((section) =>
        filterSettingsByCapabilities({
          devices,
          section,
        })
      )
      .map(outdoorSensorTransform(devices))
      // Filter indoor climate settings
      .filter((section) =>
        filterIndoorClimateSections({
          devices,
          section,
        })
      )
      // Filter out settings that should be hidden in bulk mode
      .map((section) => filterBulkModeSettings({ section, inBulkMode }))
      // Hide sections without settings:
      .filter((section) => section.contents.length > 0)
      // Conditionally show Notify about noise setting for devices with this property set
      .map((section) =>
        showNotifyAboutNoise({
          showNotifyAboutNoise: !!featureFlags?.showNotifyAboutNoise,
          presets: !!featureFlags?.presets,
          section,
        })
      )
  )
}

export function getCurrentConfigs(
  devices: TDevice[],
  extras: {
    clockType: TClockTypeMaybe
    temperature_unit: TTemperatureUnit
  }
): IDeviceSettings[] {
  return devices.map((device) => {
    return { ...device, ...extras }
  })
}
function outdoorSensorTransform(devices: TDevice[]) {
  return (section: ISection<IDeviceSettings, IDeviceSettingsPayload>) => {
    if (section.key !== GENERAL_SETTINGS_SECTION) {
      return section // No need to filter out outdoor sensor setting
    }
    const showOutdoorToggle = devices.every((d) => d.outdoor_support)
    if (showOutdoorToggle) {
      return section // sensor(s) have outdoor support, no filtering needed
    }

    // No support; remove outdoor sensor setting:
    return {
      ...section,
      contents: section.contents.filter(
        (c) =>
          c.type !== EditType.TOGGLE ||
          (c.type === EditType.TOGGLE && c.id !== OUTDOOR_SENSOR_ID)
      ),
    }
  }
}

function filterIndoorClimateSections({
  devices,
  section,
}: {
  devices: TDevice[]
  section: ISection<IDeviceSettings, IDeviceSettingsPayload>
}) {
  const inBulkMode = devices.length > 1

  const isOldTemperatureOrHumiditySection = [
    HUMIDITY_SECTION_KEY,
    TEMPERATURE_SECTION_KEY,
  ].includes(section.key)

  const isTemperatureOrHumiditySectionV2 = [
    TEMPERATURE_SECTION_KEY_V2,
    HUMIDITY_SECTION_KEY_V2,
  ].includes(section.key)

  const hasHumiditySupport = devices.every((d) =>
    d.capabilities.includes("humidity_threshold")
  )
  const hasTemperatureSupport = devices.every((d) =>
    d.capabilities.includes("temperature_threshold")
  )

  // Don't show ICM settings if device is placed outdoors
  const isPlacedOutdoors = devices.every((d) => d.placed_outdoors)

  const hasAnyICMSupport = hasHumiditySupport || hasTemperatureSupport

  if (
    isPlacedOutdoors &&
    (isOldTemperatureOrHumiditySection || isTemperatureOrHumiditySectionV2)
  ) {
    return false
  }

  if (
    !hasAnyICMSupport &&
    (isOldTemperatureOrHumiditySection || isTemperatureOrHumiditySectionV2)
  ) {
    return false
  }

  if (inBulkMode && isTemperatureOrHumiditySectionV2) {
    return false
  }

  if (!inBulkMode && isOldTemperatureOrHumiditySection) {
    return false
  }

  return true
}

function showNotifyAboutNoise({
  showNotifyAboutNoise,
  presets,
  section,
}: {
  showNotifyAboutNoise: boolean
  presets: boolean
  section: ISection<IDeviceSettings, IDeviceSettingsPayload>
}) {
  if (presets && showNotifyAboutNoise) {
    return section
  }

  return {
    ...section,
    contents: section.contents.filter(
      (c) => !(c.type === EditType.DROPDOWN && c.id === NOTIFY_ABOUT_NOISE_ID)
    ),
  }
}

function filterAdvancedSettings(
  setting: ISection<
    IDeviceSettings,
    IDeviceSettingsPayload
  >["contents"][number],
  devices: TDevice[]
): boolean {
  if (devices.every((d) => isWaterLeakDetectionDevice(d))) {
    return WaterLeakSectionAdvancedContents.includes(setting)
  }

  if (devices.every((d) => isEurekaDevice(d))) {
    return EurekaSectionAdvancedContents.includes(setting)
  }

  return true
}

export function filterSettingsByCapabilities({
  devices,
  section,
}: {
  devices: TDevice[]
  section: ISection<IDeviceSettings, IDeviceSettingsPayload>
}): ISection<IDeviceSettings, IDeviceSettingsPayload> {
  // Filter out settings that don't have matching capabilities
  const filteredContents = section.contents.filter((setting) => {
    if (section.key === ADVANCED_SETTINGS_SECTION) {
      return filterAdvancedSettings(setting, devices)
    }

    if (setting.type === EditType.NODE) {
      return devices.every((device) =>
        shouldDisplaySetting(setting.key, device.capabilities)
      )
    }

    return true
  })

  return {
    ...section,
    contents: filteredContents,
  }
}
